import { yupResolver } from '@hookform/resolvers/yup'
import { EFinancingCompany } from '@if/core/types'
import { formatDate, normalizeDateTime } from '@if/core/utils'
import * as yup from '@if/core/yup'
import { ControlledAutocomplete, ControlledNumberField, ControlledStringDateOnlyPicker } from '@if/mui'
import { Button, Grid, InputAdornment } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { FormatPromoCode } from '@src/containers/App/utils/Formatter'
import { useSearchPaymentPlans } from '@src/data/api/merchants-api/merchants-api'
import { ApplicableMerchantPaymentPlan } from '@src/data/types/ApplicableMerchantPaymentPlan'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

interface Props {
  open: boolean
  handleClose: () => void
  merchantFinancingCompanyId: EFinancingCompany
  onMerchantPaymentPlanAdded: (dto: ApplicableMerchantPaymentPlan) => void
}

const CreateMerchantPaymentPlanDialog = ({
  open,
  handleClose,
  merchantFinancingCompanyId,
  onMerchantPaymentPlanAdded,
}: Props) => {
  const { t } = useTranslation()

  const CreateMerchantPaymentPlanSchema = useMemo(() => {
    return yup.object().shape({
      paymentPlan: yup
        .object()
        .shape({
          id: yup.string().required(),
          promoCode: yup.string().required(),
          activeFrom: yup.date().required(),
          expiresOn: yup.date().nullable(),
        })
        .nullable()
        .test('payment-plan-required', t('merchantPaymentPlansPanel.errors.promoRequired'), (value) => value !== null),
      merchantFeeRate: yup.number().min(0).max(100).required(),
      borrowerFeeRate: yup.number().min(0).max(100).required(),
      activeFrom: yup
        .string()
        .test(
          'min-payment-plan-date',
          t('merchantPaymentPlansPanel.errors.activationDateMustBeLaterThanPromoActivation'),
          function (value) {
            const { paymentPlan } = this.parent as { paymentPlan?: { activeFrom: Date } }
            if (!value || !paymentPlan?.activeFrom) return false
            return normalizeDateTime(value) >= normalizeDateTime(paymentPlan.activeFrom)
          },
        )
        .required(),
      expiresOn: yup
        .string()
        .nullable()
        .test(
          'max-payment-plan-date',
          t('merchantPaymentPlansPanel.errors.expirationDateMustBeEarlierThanPromoExpiration'),
          function (value) {
            const { paymentPlan } = this.parent as { paymentPlan?: { expiresOn?: Date } }
            if (!paymentPlan?.expiresOn) return true
            if (!value) return false
            return normalizeDateTime(value) <= normalizeDateTime(paymentPlan.expiresOn)
          },
        )
        .test(
          'min-active-from-date',
          t('merchantPaymentPlansPanel.errors.expirationDateMustBeLaterThanActivation'),
          function (value) {
            const { activeFrom } = this.parent as { activeFrom: Date }
            if (!value) return true
            return normalizeDateTime(value) >= normalizeDateTime(activeFrom)
          },
        ),
    })
  }, [t])

  type CreateMerchantPaymentPlanSchemaType = yup.InferType<typeof CreateMerchantPaymentPlanSchema>

  const [paymentPlans, isLoadingPaymentPlans] = useSearchPaymentPlans({
    isActive: true,
    financingCompanyId: merchantFinancingCompanyId,
  })

  const { control, watch, reset, handleSubmit, setValue } = useForm<CreateMerchantPaymentPlanSchemaType>({
    mode: 'onBlur',
    defaultValues: { paymentPlan: null, activeFrom: formatDate(new Date()) },
    resolver: yupResolver(CreateMerchantPaymentPlanSchema),
  })
  const paymentPlan = watch('paymentPlan')
  const activeFrom = watch('activeFrom')
  const expiresOn = watch('expiresOn')

  useEffect(() => {
    if (!paymentPlan) return
    setValue('expiresOn', formatDate(paymentPlan.expiresOn))
  }, [paymentPlan, setValue])

  const closeDialog = () => {
    handleClose()
    reset()
  }

  const onSubmit = (formData: CreateMerchantPaymentPlanSchemaType) => {
    const paymentPlan = paymentPlans.find((pp) => pp.id === formData.paymentPlan?.id)
    if (!paymentPlan) {
      throw new Error('payment plan not selected')
    }

    const plan: ApplicableMerchantPaymentPlan = {
      id: `creation-pending-${Number(new Date())}`,
      creationPending: true,
      paymentPlanId: paymentPlan.id,
      activeFrom: normalizeDateTime(formData.activeFrom),
      expiresOn: formData.expiresOn ? normalizeDateTime(formData.expiresOn) : null,
      borrowerFeeRate: formData.borrowerFeeRate,
      merchantFeeRate: formData.merchantFeeRate,
      promoCode: paymentPlan.promoCode,
      reducedInterestRate: paymentPlan.reducedInterestRate,
      interestRate: paymentPlan.interestRate,
      reducedRateDurationInMonths: paymentPlan.reducedRateDurationInMonths,
      loanTerm: paymentPlan.loanTerm,
    }
    onMerchantPaymentPlanAdded(plan)
    closeDialog()
  }

  return (
    <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="md">
      <DialogTitle>{t('merchantPaymentPlansPanel.createMerchantPaymentPlan')}</DialogTitle>
      <DialogContent>
        <Grid container columns={12} spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledAutocomplete
              name="paymentPlan"
              control={control}
              label={t('merchant.promo.promoCode')}
              fullWidth
              options={paymentPlans}
              getOptionLabel={(option) => (option ? FormatPromoCode(option.promoCode) : '')}
              getOptionKey={(option) => option?.id || ''}
              loading={isLoadingPaymentPlans}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}></Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledNumberField
              name="borrowerFeeRate"
              control={control}
              label={t('merchant.ratesAndFees.adminFeeCustomer')}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledNumberField
              name="merchantFeeRate"
              control={control}
              label={t('merchant.ratesAndFees.adminFeeMerchant')}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledStringDateOnlyPicker
              name="activeFrom"
              control={control}
              label={t('merchant.promo.activeFrom')}
              fullWidth
              minDate={paymentPlan ? normalizeDateTime(paymentPlan.activeFrom) : undefined}
              disabled={!paymentPlan}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <ControlledStringDateOnlyPicker
              name="expiresOn"
              control={control}
              label={expiresOn ? t('merchant.promo.expiresOn') : t('merchant.promo.noExpiresOn')}
              fullWidth
              minDate={normalizeDateTime(activeFrom)}
              disabled={!paymentPlan}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)}>
          {t('common.confirm')}
        </Button>
        <Button variant="contained" onClick={closeDialog}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateMerchantPaymentPlanDialog
